import moment from "moment";
import React, { useContext, useState, useEffect, useMemo } from "react";
import feather from "feather-icons";

import { AppContexts } from "../../../providers";
import ReservationNumber from "../../common/ReservationNumber";
import PropertyDetailsModal from "../../common/PropertyDetailsModal";
import { getReservationDoorCode } from "./reservation-door-code";
import BookingFinancials from "./booking-financials";
import PropertyTags from "./property-tags";

import { host } from "../../../http";
import { ReactComponent as ContractStatusIcon } from "../../../assets/icons/status/contract.svg";

const hasValue = (value) => value != null && String(value).trim() !== "";

const FeatherIcon = ({ name, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  return (
    <svg
      className={`feather-icon ${className}`}
      viewBox="0 0 24 24"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
      style={{ color: "var(--dark-purple)" }}
    />
  );
};

const sortByCheckInDesc = (a, b) =>
  moment(b.check_in, "YYYY-MM-DD").valueOf() -
  moment(a.check_in, "YYYY-MM-DD").valueOf();

const sortByCheckInAsc = (a, b) =>
  moment(a.check_in, "YYYY-MM-DD").valueOf() -
  moment(b.check_in, "YYYY-MM-DD").valueOf();

const GuestDetails = (props) => {
  const { contact = null, guest_reservations = [], guest = null } = props;
  const threadInfo =
    !guest || guest?.guest_id == "0" ? contact || {} : guest || {};
  const { user } = useContext(AppContexts.AuthContext);

  const [indexes, setIndexes] = useState({
    current: 0,
    upcoming: 0,
    past: 0,
  });
  const [currentReservation, setCurrentReservation] = useState({});
  const [propertyDetailsOpen, setPropertyDetailsOpen] = useState(false);
  const [activeReservationTab, setActiveReservationTab] = useState("current");

  const sortedReservations = useMemo(() => {
    const current = [],
      upcoming = [],
      past = [];

    const today = moment().startOf("day");
    guest_reservations.forEach((it) => {
      const checkIn = moment(it.check_in, "YYYY-MM-DD");
      const checkOut = moment(it.check_out, "YYYY-MM-DD");

      if (checkIn.isAfter(today)) {
        upcoming.push(it);
      } else if (checkOut.isBefore(today)) {
        past.push(it);
      } else {
        current.push(it);
      }
    });
    return {
      current: current.sort(sortByCheckInAsc),
      upcoming: upcoming.sort(sortByCheckInAsc),
      past: past.sort(sortByCheckInDesc),
    };
  }, [guest_reservations]);

  const incrementIndex = (tab) => {
    const length = sortedReservations[tab].length;
    setIndexes({ ...indexes, [tab]: (indexes[tab] + 1) % length });
  };

  const decrementIndex = (tab) => {
    const length = sortedReservations[tab].length;
    setIndexes({ ...indexes, [tab]: (indexes[tab] - 1 + length) % length });
  };

  const reservationRenderer = (tab) => {
    const reservation = sortedReservations[tab][indexes[tab]];
    if (!reservation) return null;

    const doorCode = getReservationDoorCode(reservation);
    const stayDates = [reservation.check_in, reservation.check_out]
      .filter((date) => hasValue(date) && moment(date).isValid())
      .map((date) => moment(date).format("MMM DD, YYYY"));
    const bookingSource = reservation?.formatted_source || reservation?.source;

    return (
      <div className="guest-chat-info-wrapper">
        {sortedReservations[tab].length > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              Booking {indexes[tab] + 1} of {sortedReservations[tab].length}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <span
                className="uk-icon-button"
                onClick={() => decrementIndex(tab)}
              >
                <FeatherIcon
                  name="chevron-left"
                  className="guest-detail-icon"
                />
              </span>
              <span
                className="uk-icon-button"
                onClick={() => incrementIndex(tab)}
              >
                <FeatherIcon
                  name="chevron-right"
                  className="guest-detail-icon"
                />
              </span>
            </div>
          </div>
        )}

        {reservation.category === "CXL" && (
          <div className="hms-booking-tag uk-margin-small-top cancelled">
            Cancelled Booking
          </div>
        )}
        {stayDates.length > 0 && (
          <div className="uk-margin-small-top message-text-group">
            <span uk-tooltip="Check-In - Check-Out">
              <FeatherIcon name="calendar" className="guest-detail-icon" />
            </span>
            <div className="chat-value">{stayDates.join(" - ")}</div>
          </div>
        )}

        {(hasValue(reservation?.pms_id) || reservation?.rental_id) && (
          <div className="uk-margin-small message-text-group">
            <span uk-tooltip="Property ID">
              <FeatherIcon name="home" className="guest-detail-icon" />
            </span>
            <div className="chat-value">
              {hasValue(reservation?.pms_id) && <div>{reservation.pms_id}</div>}
              {reservation?.rental_id && (
                <div style={{ textAlign: "left" }}>
                  <button
                    type="button"
                    className="message-property-details-button"
                    aria-label="Property Values"
                    aria-haspopup="dialog"
                    uk-tooltip="Imported and custom property values"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentReservation(reservation);
                      setPropertyDetailsOpen(true);
                    }}
                  >
                    <span aria-hidden="true">
                      <FeatherIcon name="list" />
                    </span>
                    Property Values
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {hasValue(reservation?.pms_address) && (
          <div className="uk-margin-small message-text-group">
            <span uk-tooltip="Address">
              <FeatherIcon name="map-pin" className="guest-detail-icon" />
            </span>
            <div className="chat-value">{reservation?.pms_address}</div>
          </div>
        )}

        {hasValue(
          user?.details?.pms === "Streamline" && String(reservation?.host_id) !== "6413949926899712"
            ? reservation?.native_id
            : reservation?.reservation_id,
        ) && (
          <div className="uk-margin-small message-text-group">
            <span uk-tooltip="Reservation Number">
              <FeatherIcon name="hash" className="guest-detail-icon" />
            </span>
            <div className="chat-value">
              <ReservationNumber
                hostID={reservation?.host_id}
                pms={user?.details?.pms}
                resevationID={reservation?.reservation_id}
                nativeID={reservation?.native_id}
              />
            </div>
          </div>
        )}

        <div className="uk-margin-small message-text-group">
          <span uk-tooltip="Door Code">
            <FeatherIcon name="lock" className="guest-detail-icon" />
          </span>
          <div className="chat-value">
            {hasValue(doorCode) ? doorCode : <span className="secondary">Empty</span>}
          </div>
        </div>

        {hasValue(bookingSource) && (
          <div className="uk-margin-small message-text-group">
            <span uk-tooltip="Booking Source">
              <FeatherIcon name="globe" className="guest-detail-icon" />
            </span>
            <div className="chat-value">{bookingSource}</div>
          </div>
        )}

        {reservation?.guest_reservation_id && (
          <div className="uk-margin-small message-text-group">
            <span uk-tooltip="Guest Book">
              <FeatherIcon name="book-open" className="guest-detail-icon" />
            </span>
            <div className="chat-value">
              <a
                href={`${host}/v2/reservations/guestbook/${reservation.guest_reservation_id}`}
                target="_blank"
                rel="noreferrer"
              >
                Guestbook
              </a>
            </div>
          </div>
        )}

        {hasValue(reservation?.contract_signed) &&
          (user?.details?.pms === "Streamline" ||
          user?.details?.pms === "LiveRez" ||
          user?.details?.pms === "Hostaway" ||
          user?.details?.pms === "OwnerRez") && (
          <div className="uk-margin-small message-text-group">
            <span
              uk-tooltip={
                reservation?.contract_signed
                  ? "Contract Signed"
                  : "Contract Not Signed"
              }
            >
              <ContractStatusIcon
                className={`guest-detail-icon reservation-status-icon--${reservation?.contract_signed ? "success" : "warning"}`}
                aria-hidden="true"
              />
            </span>

            <div className="chat-value">
              {reservation?.contract_signed ? (
                <span style={{ color: "var(--green)" }}>Contract Signed</span>
              ) : (
                <span className="reservation-status-text--warning">
                  Contract Not Signed
                </span>
              )}
            </div>
          </div>
        )}

        <BookingFinancials reservation={reservation} pms={user?.details?.pms} />
      </div>
    );
  };

  const reservationTabs = ["current", "upcoming", "past"].filter(
    (tab) => sortedReservations[tab].length > 0,
  );
  const selectedReservationTab = sortedReservations[activeReservationTab].length
    ? activeReservationTab
    : reservationTabs[0];

  const [contactProperties, setContactProperties] = useState([]);

  useEffect(() => {
    setContactProperties([]);
    let active = true;
    const id = contact?.id;
    if (!id) return;

    if (Array.isArray(contact?.rentals) && contact.rentals.length) {
      setContactProperties(contact.rentals);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${host}/v2/contacts/${id}?include=rentals`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to load contact w/ rentals");
        const data = await res.json();

        const rentals = (data?.rentals ?? []).map((r) => ({
          rental_id: r.rental_id ?? r.id ?? r.rentalId,
          pms_id: r.pms_id ?? r.pmsId ?? r.code ?? r.pms_code,
        }));

        if (!active) return;
        setContactProperties(
          rentals
            .filter((r) => r.rental_id || r.pms_id)
            .sort((a, b) =>
              String(a.pms_id || "").localeCompare(String(b.pms_id || "")),
            ),
        );
      } catch (e) {
        if (!active) return;
        console.warn("contact rentals fetch failed:", e);
        setContactProperties([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [contact?.id, contact?.rentals]);

  return (
    <div className="hms-booking-info-wrapper">
      {/* Guest / Contact identity card */}
      <div className="hms-guest-identity-card">
        <div className="message-text-group guest-info">
          <span
            className="message-text-group-icon"
            uk-tooltip={`title: ${threadInfo.category?.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase()) || "Guest"}; pos: bottom-left; cls: uk-active contact-tooltip`}
          >
            <img
              src={`/images/${threadInfo.category || "guest"}-active.svg`}
              alt=""
              width="30"
              height="30"
            />
          </span>
          <div className="uk-width-expand">
            <div className="guest-chat-info">
              <div className="chat-guest-value semi-bold-font">
                {threadInfo.first_name} {threadInfo.last_name}
              </div>
              {hasValue(threadInfo.formatted_phone) && (
                <div className="chat-guest-value">{threadInfo.formatted_phone}</div>
              )}
              {hasValue(threadInfo.email) && (
                <div className="chat-guest-value">{threadInfo.email}</div>
              )}

              {!threadInfo.category && (
                <div className="secondary">
                  {guest_reservations.length} booking(s)
                </div>
              )}
            </div>
          </div>
        </div>

        {contactProperties.length > 0 && (
          <div className="uk-flex uk-flex-top uk-margin-top">
            <FeatherIcon name="home" className="guest-detail-icon" />

            <div className="hms-guest-tags">
              <div className="guest-tags uk-flex uk-flex-wrap">
                {contactProperties.map((p) => (
                  <div
                    className="uk-inline"
                    key={String(p.rental_id ?? p.pms_id)}
                  >
                    {p.pms_id || `#${p.rental_id}`}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Booking details card */}
      {!threadInfo.category && reservationTabs.length > 0 && (
        <div className="uk-card uk-card-default uk-card-small uk-card-body hms-msg-cards hms-booking-info">
          <div
            className="bookings rbr-pill-toggle rbr-task-status-toggle"
            role="tablist"
            aria-label="Reservations"
          >
            {reservationTabs.map((tab) => {
              const label =
                tab === "current"
                  ? "Current"
                  : tab === "upcoming"
                    ? "Upcoming"
                    : "Past";
              const isActive = selectedReservationTab === tab;

              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`rbr-pill-toggle-button${isActive ? " is-active" : ""}`}
                  key={tab}
                  onClick={() => setActiveReservationTab(tab)}
                >
                  {label} ({sortedReservations[tab].length})
                </button>
              );
            })}
          </div>
          <div className="uk-margin-small-top">
            {selectedReservationTab &&
              reservationRenderer(selectedReservationTab)}
          </div>
        </div>
      )}

      <PropertyTags
        properties={threadInfo.category
          ? contactProperties
          : [sortedReservations[selectedReservationTab]?.[indexes[selectedReservationTab]]].filter(Boolean)}
      />

      <PropertyDetailsModal
        open={propertyDetailsOpen}
        rentalId={currentReservation?.rental_id}
        onClose={() => setPropertyDetailsOpen(false)}
      />
    </div>
  );
};

export default GuestDetails;
